import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuthStore } from '@/stores/authStore';
import { authService, getErrorMessage } from '@/services';
import { Eye, EyeOff, LogIn, Zap, ArrowRight } from 'lucide-react';
import toast from 'react-hot-toast';

const loginSchema = z.object({
  username: z.string().min(1, 'validation.required'),
  password: z.string().min(1, 'validation.required'),
});

type LoginForm = z.infer<typeof loginSchema>;

export default function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login } = useAuthStore();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginForm) => {
    setIsLoading(true);
    try {
      const response = await authService.login(data);

      login(response.user, response.accessToken);
      toast.success(t('common.success'));
      if (response.user.role === 'admin') {
        navigate('/admin');
      } else if (!response.user.setupCompleted) {
        navigate('/setup');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message || t('auth.loginError'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="animate-fade-in">
      {/* Card */}
      <div className="bg-white border border-dark-700 rounded-lg p-8 shadow-card">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-lg bg-primary-600 mb-4 shadow-sm">
            <Zap size={26} className="text-white" />
          </div>
          <h1 className="text-2xl font-bold text-dark-100 mb-1">{t('auth.welcome')}</h1>
          <p className="text-dark-400 text-sm">{t('auth.welcomeMessage')}</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          {/* Username */}
          <div>
            <label htmlFor="username" className="label">
              {t('auth.username')}
            </label>
            <input
              id="username"
              type="text"
              autoComplete="username"
              placeholder={t('auth.usernamePlaceholder')}
              className={errors.username ? 'input-error' : 'input'}
              {...register('username')}
            />
            {errors.username && (
              <p className="mt-2 text-sm text-error-600 flex items-center gap-1">
                <span className="w-1 h-1 rounded-full bg-error-500" />
                {t(errors.username.message || '')}
              </p>
            )}
          </div>

          {/* Password */}
          <div>
            <label htmlFor="password" className="label">
              {t('auth.password')}
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder={t('auth.passwordPlaceholder')}
                className={errors.password ? 'input-error pr-12' : 'input pr-12'}
                {...register('password')}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-dark-500 hover:text-dark-300 hover:bg-dark-800 transition-all"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {errors.password && (
              <p className="mt-2 text-sm text-error-600 flex items-center gap-1">
                <span className="w-1 h-1 rounded-full bg-error-500" />
                {t(errors.password.message || '')}
              </p>
            )}
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={isLoading}
            className="btn-primary w-full py-3 mt-2 group"
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
                {t('auth.loggingIn')}
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <LogIn size={18} />
                {t('auth.login')}
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </span>
            )}
          </button>
        </form>

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-dark-700 text-center">
          <p className="text-xs text-dark-500">
            DAZN Panel CMS &bull; Professional Streaming Manager
          </p>
        </div>
      </div>
    </div>
  );
}
